import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { ChevronRight, CalendarRange } from 'lucide-react';
import styled from 'styled-components';
import api from '../../../services/api';
import {
  Card,
  CardHeader,
  CardTitle,
  EmptyState,
} from '../styles';

const ClickableHeader = styled(CardHeader)`
  cursor: pointer;
  user-select: none;
`;

const HeaderLink = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  color: ${({ theme }) => theme.colors.primary.main};
  font-size: 12px;
  font-weight: 600;
`;

const PeriodList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding: 12px 16px 16px;
`;

const PeriodRow = styled.div`
  cursor: pointer;
`;

const PeriodHead = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 6px;
`;

const PeriodName = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const StatusPill = styled.span`
  font-size: 11px;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 10px;
  background: ${(p) => p.$bg};
  color: ${(p) => p.$color};
`;

const ProgressBarOuter = styled.div`
  width: 100%;
  height: 8px;
  background: #f3f4f6;
  border-radius: 4px;
  overflow: hidden;
`;

const ProgressBarInner = styled.div`
  height: 100%;
  background: ${(p) => p.$bg};
  width: ${(p) => p.$pct}%;
  transition: width 0.3s ease;
`;

const ProgressMeta = styled.div`
  font-size: 11px;
  color: #6b7280;
  margin-top: 4px;
  display: flex;
  justify-content: space-between;
`;

const STATUS_COLORS = {
  draft:     { color: '#64748B', bg: '#F1F5F9' },
  active:    { color: '#0291EF', bg: '#EFF6FF' },
  reminding: { color: '#EA580C', bg: '#FFF7ED' },
  closed:    { color: '#9333EA', bg: '#FAF5FF' },
  activated: { color: '#16A34A', bg: '#DCFCE7' },
};

const PROGRESS_BAR_BG = {
  draft: '#94A3B8',
  active: '#0291EF',
  reminding: '#EA580C',
  closed: '#9333EA',
  activated: '#16A34A',
};

export default function OpenEnrollmentProgressCard() {
  const { t } = useTranslation();
  const history = useHistory();
  const [periods, setPeriods] = useState([]);

  useEffect(() => {
    // Show only currently-active or upcoming periods (active/reminding/closed)
    api.get('/v1/open-enrollment-periods', { params: { pageNumber: 1, pageSize: 5 } })
      .then((res) => {
        const result = res.data?.data;
        const items = result?.data || [];
        const live = items.filter((p) => p.status === 'active' || p.status === 'reminding' || p.status === 'closed');
        setPeriods(live.length > 0 ? live : items.slice(0, 3));
      })
      .catch(() => {});
  }, []);

  const goToList = () => history.push('/open-enrollment-periods');

  return (
    <Card $delay="0.3s">
      <ClickableHeader onClick={goToList}>
        <CardTitle>{t('dashboard.openEnrollmentProgress')}</CardTitle>
        <HeaderLink>
          {t('common.seeAll')}
          <ChevronRight size={14} />
        </HeaderLink>
      </ClickableHeader>
      {periods.length === 0 ? (
        <EmptyState>
          <CalendarRange size={28} />
          {t('dashboard.noOpenEnrollment')}
        </EmptyState>
      ) : (
        <PeriodList>
          {periods.map((p) => {
            const eligible = p.eligibleEmployeeCount || 0;
            const submitted = p.submittedSelectionCount || 0;
            const pct = eligible > 0 ? Math.min(100, Math.round((submitted * 100) / eligible)) : 0;
            const statusCfg = STATUS_COLORS[p.status] || STATUS_COLORS.draft;
            return (
              <PeriodRow key={p.id} onClick={() => history.push('/open-enrollment-periods')}>
                <PeriodHead>
                  <PeriodName>{p.name}</PeriodName>
                  <StatusPill $color={statusCfg.color} $bg={statusCfg.bg}>
                    {t(`openEnrollment.status${p.status?.charAt(0).toUpperCase()}${p.status?.slice(1)}`, { defaultValue: p.status })}
                  </StatusPill>
                </PeriodHead>
                <ProgressBarOuter>
                  <ProgressBarInner $pct={pct} $bg={PROGRESS_BAR_BG[p.status] || '#94A3B8'} />
                </ProgressBarOuter>
                <ProgressMeta>
                  <span>{submitted} / {eligible} {t('dashboard.openEnrollmentSubmitted')}</span>
                  <span>{pct}%</span>
                </ProgressMeta>
              </PeriodRow>
            );
          })}
        </PeriodList>
      )}
    </Card>
  );
}
